import fromExponential from 'from-exponential';

import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

import { MIN_PER_HOUR, NO_VALUE, SEC_PER_MIN, WIND_DIRECTIONS_CLOCKWISE_FROM_N } from './constants';
import { FormattedUnitLabels, FormatUnitsValue, RawValue, UnitValue } from './types';

/** Narrow caller input to {@link RawValue} for the unit pipeline. */
export function toRawValue(value: FormatUnitsValue): RawValue {
  if (
    value === undefined ||
    value === null ||
    typeof value === 'number' ||
    typeof value === 'string' ||
    typeof value === 'boolean'
  ) {
    return value;
  }
  return undefined;
}

/**
 * Scales a boolean value to a given true or false value.
 * @param rawValue - The value to scale.
 * @param scaledTrueVal - The value to return if the value is true.
 * @param scaledFalseVal - The value to return if the value is false.
 * @returns The scaled value.
 */
export const scaleBooleanValue = <T>(
  rawValue: boolean | string | number | null | undefined,
  scaledTrueVal: T,
  scaledFalseVal: T
): T => {
  if (rawValue === true || ['true', 'on'].includes(String(rawValue).toLowerCase())) {
    return scaledTrueVal;
  }
  return scaledFalseVal;
};

/**
 * Converts a string, number, or boolean to a number.
 * @param rawValue - The value to convert.
 * @returns The converted number or null if it failed.
 */
export const toNumber = (rawValue: string | number | boolean | null | undefined): number | null => {
  if (isNullOrUndefined(rawValue)) return null;

  switch (typeof rawValue) {
    case 'string': {
      const value = parseFloat(rawValue);
      if (isNaN(value)) return null;

      return value;
    }
    case 'number':
      return rawValue;
    case 'boolean':
    default:
      return null;
  }
};

/**
 * Formats a number to a given precision.
 * @param rawValue - The value to format.
 * @param precision - The precision to format the number to.
 * @returns The formatted number or null if it failed.
 */
export const formatNumber = (
  rawValue: string | number | boolean | null | undefined,
  precision: number = 2
): number | null => {
  const value = toNumber(rawValue);
  if (value == null) return null;

  return +value.toFixed(precision);
};

/**
 * Formats a very small number to show 2 non-zero decimal digits (or 1 if only 1 exists).
 * Expands via from-exponential first and returns a decimal string so display never
 * reintroduces scientific notation via `${value}` interpolation.
 */
export const formatTwoNonZeroDecimals = (value: number): string => {
  const valueStr = fromExponential(value).toString();
  const [integerPart, decimalPart = ''] = valueStr.split('.');

  let firstNonZeroIndex = -1;
  let nonZeroCount = 0;
  let lastNonZeroIndex = -1;

  for (let i = 0; i < decimalPart.length; i++) {
    if (decimalPart[i] !== '0') {
      if (firstNonZeroIndex === -1) {
        firstNonZeroIndex = i;
      }
      nonZeroCount++;
      lastNonZeroIndex = i;
      if (nonZeroCount === 2) {
        break;
      }
    }
  }

  if (firstNonZeroIndex !== -1 && nonZeroCount >= 1) {
    const truncatedDecimal = decimalPart.substring(0, lastNonZeroIndex + 1);
    return `${integerPart}.${truncatedDecimal}`;
  }

  return valueStr;
};

/**
 * Converts a number to an integer.
 * @param num - The number to convert.
 * @param numStr - Optional precomputed Number#toString() to avoid redundant string work.
 * @returns The integer value.
 */
const floatToInt = (num: any, numStr?: string) => {
  const str = numStr ?? Number(num).toString();
  return Number(str.replace('.', ''));
};

/**
 * Gets the length of the decimal part of a number.
 * @param num - The number to get the length of the decimal part of.
 * @param numStr - Optional precomputed Number#toString() to avoid redundant string work.
 * @returns The length of the decimal part of the number.
 */
const digitLength = (num: any, numStr?: string) => {
  const str = numStr ?? Number(num).toString();
  return (str.split('.')[1] || '').length;
};

/**
 * Truncates a number to a given precision.
 * @param num - The number to truncate.
 * @param precision - The precision to truncate the number to.
 * @returns The truncated number.
 */
const truncate = (num: any, precision: number) => {
  return parseFloat(Number(num).toPrecision(precision));
};

/** True when a Number#toString() result contains scientific notation (e.g. 1e-9, 6.2137e-7). */
const usesExponentialNotationFromString = (numStr: string): boolean => /e/i.test(numStr);

const fpScaleInts = (numeratorInt: number, denominatorInt: number, decimalShift: number): number =>
  (numeratorInt * denominatorInt) / Math.pow(10, decimalShift);

/**
 *  fpMultiply - floating point multiplication
 *
 *  multiply, maintaining precision
 * for example in JS: .585 * 60 => 35.099999999999994
 * but, fpMultiply(.585, 60) => 35.1
 *
 * When either operand's Number#toString() contains scientific notation (e/E), native
 * IEEE-754 multiplication is used because floatToInt/digitLength are unreliable on
 * exponential strings.
 *
 * @param a - The first number to multiply.
 * @param b - The second number to multiply.
 * @returns The multiplied number.
 */
export const fpMultiply = (a: any, b: any) => {
  const aNum = Number(a);
  const bNum = Number(b);
  const aStr = aNum.toString();
  const bStr = bNum.toString();
  if (usesExponentialNotationFromString(aStr) || usesExponentialNotationFromString(bStr)) {
    return aNum * bNum;
  }

  const aInt = floatToInt(aNum, aStr);
  const bInt = floatToInt(bNum, bStr);
  const base = digitLength(aNum, aStr) + digitLength(bNum, bStr);

  return fpScaleInts(aInt, bInt, base);
};

/**
 *  fpDivide - floating point division
 *
 *  divide, maintaining precision
 * for example in JS: 35.1 / 60 => 0.5850000000000001
 * but, fpDivide(35.1, 60) => .585
 *
 * When either operand's Number#toString() contains scientific notation (e/E), native
 * IEEE-754 division is used because floatToInt/digitLength are unreliable on
 * exponential strings.
 *
 * @param a - The first number to divide.
 * @param b - The second number to divide.
 * @returns The divided number.
 */
export const fpDivide = (a: any, b: any) => {
  const aNum = Number(a);
  const bNum = Number(b);
  const aStr = aNum.toString();
  const bStr = bNum.toString();
  if (usesExponentialNotationFromString(aStr) || usesExponentialNotationFromString(bStr)) {
    return aNum / bNum;
  }

  const aInt = floatToInt(aNum, aStr);
  const bInt = floatToInt(bNum, bStr);
  const base = digitLength(bNum, bStr) - digitLength(aNum, aStr);
  const precision = truncate(Math.pow(10, base), 8);

  return fpMultiply(aInt / bInt, precision);
};

/**
 * Binds compile-time pivot factors to fpMultiply/fpDivide at transformer init.
 * Used by createPivotTransformer so factor closures are created once per pivot key.
 */
export const createFpFactorOps = (factor: number) => ({
  multiplyBy: (value: number): number => fpMultiply(value, factor),
  divideBy: (value: number): number => fpDivide(value, factor),
});

/**
 * Checks if a value is numeric.
 * @param num - The value to check.
 * @returns True if the value is numeric, false otherwise.
 */
export const isNumeric = (num: any) => {
  return (
    (typeof num === 'number' && !isNaN(num)) || (typeof num === 'string' && !isNaN(parseFloat(num)))
  );
};

/**
 * Converts a string to a number and scales it by a given factor.
 * @param value - The string to convert.
 * @param scale - The factor to scale the number by.
 * @returns The scaled number or null if it failed.
 */
export const floatHandler = (value: string, scale: number) => {
  let v = parseFloat(value);
  if (isNaN(v)) return null;
  if (scale) {
    v /= scale;
  }
  v = +v.toFixed(2);
  return v;
};

/**
 * Converts a UnitValue to a FormattedUnitLabels object.
 * @param unitValue - The UnitValue to convert.
 * @returns The FormattedUnitLabels object.
 */
export const unitValueToFormattedUnitLabels = (unitValue: UnitValue): FormattedUnitLabels => {
  const unitsIsEmpty = isNullOrUndefined(unitValue.units) || unitValue.units === '';
  const valueIsEmpty = isNullOrUndefined(unitValue.value);

  const formattedValue = valueIsEmpty ? NO_VALUE : unitValue.value;
  const formattedUnits = unitsIsEmpty || valueIsEmpty ? '' : unitValue.units;

  const valueLabel = `${formattedValue}`;
  const unitsLabel = `${formattedUnits}`;

  const valueUnitsLabel = `${formattedValue}${unitsIsEmpty || valueIsEmpty ? '' : ' ' + formattedUnits}`;
  const suffixedValueLabel = unitsIsEmpty || valueIsEmpty ? valueLabel : valueUnitsLabel;

  return {
    valueLabel,
    unitsLabel,
    prefixedValueLabel: valueLabel,
    unitSuffixLabel: unitsLabel,
    suffixedValueLabel,
    valueUnitsLabel,
  };
};

/**
 * Converts seconds to a duration string in the format "H:MM"
 * Hours are displayed without leading zeros, minutes with leading zeros.
 * @param seconds - The number of seconds to convert
 * @returns The duration string in the format "H:MM" (e.g., "0:00", "1:23", "100:05")
 * @example
 * secondsToDuration(0) // Returns: "0:00"
 * secondsToDuration(3661) // Returns: "1:01"
 * secondsToDuration(36000) // Returns: "10:00"
 */
export const secondsToDuration = (
  seconds: number,
  hoursWithLeadingZero: boolean = false
): string => {
  const secInHr = SEC_PER_MIN * MIN_PER_HOUR;
  const hours = Math.floor(seconds / secInHr);
  const minutes = Math.floor((seconds % secInHr) / SEC_PER_MIN);
  return `${hoursWithLeadingZero ? hours.toString().padStart(2, '0') : hours}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Converts a duration string in the format "H:MM" to seconds.
 * @param duration - The duration string to convert (e.g., "1:30", "0:05", "100:00")
 * @returns The number of seconds, or null if the format is invalid
 * @example
 * durationToSeconds("0:00") // Returns: 0
 * durationToSeconds("1:01") // Returns: 3660
 * durationToSeconds("10:00") // Returns: 36000
 */
export function durationToSeconds(
  duration: string | number | boolean | null | undefined
): number | null {
  if (isNullOrUndefined(duration) || typeof duration !== 'string') return null;

  const parts = duration.split(':');
  if (parts.length !== 2) return null;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (isNaN(hours) || isNaN(minutes)) return null;

  return hours * MIN_PER_HOUR * SEC_PER_MIN + minutes * SEC_PER_MIN;
}

/**
 * Rounds to the nearest integer, then normalizes into [0, 360) via modulo.
 * Cyclic angles: -45 → 315, 370 → 10, 360 → 0.
 */
export function normalizeDegrees(degrees: number): number {
  const rounded = Math.round(degrees);
  return ((rounded % 360) + 360) % 360;
}

/**
 * Rounds to the nearest integer, then clamps to [0, 360] (no modulo wrap).
 *
 * Shared by wind animatics (`degreesFromRef` → `wind-vane.tsx`). Invalid or
 * out-of-range sensor readings are clamped, not wrapped. Callers needing cyclic
 * rotation should use `normalizeDegrees` instead.
 */
export function clampDegrees(degrees: number): number {
  const rounded = Math.round(degrees);
  return Math.max(0, Math.min(360, rounded));
}

/**
 * Maps degrees clockwise from North to an 8-point compass label.
 * Input is clamped to [0, 360] before sector lookup.
 */
export function degreesToWindDirection(degrees: number): string {
  const clamped = clampDegrees(degrees);
  const index = Math.round(clamped / 45) % 8;
  return WIND_DIRECTIONS_CLOCKWISE_FROM_N[index];
}

/**
 * True when the unit is the psig-inhg composite encoding. Valid as a source unit
 * and as a display token; never a transform target for other sources.
 * Returns false for null/undefined.
 */
export function isCompositeDisplayUnit(units?: string): boolean {
  return units?.toLowerCase() === 'psig-inhg';
}

/** True for raw gauge PSI source keys (psi, psig) — not inHg, which is gauge by reference frame only. */
export function isRawGaugePsiKey(units?: string): boolean {
  const key = units?.toLowerCase();
  return key === 'psi' || key === 'psig';
}
